import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { chatAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
    MessageSquare, 
    Send, 
    Calendar,
    Clock,
    Users,
    Plus,
    Loader2,
    Radio
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function Chat() {
    const { user } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [showCreateSession, setShowCreateSession] = useState(false);
    const [sessionForm, setSessionForm] = useState({ title: '', scheduled_at: '', description: '' });
    const messagesEndRef = useRef(null);
    const pollInterval = useRef(null);

    useEffect(() => {
        loadSessions();
        return () => {
            if (pollInterval.current) {
                clearInterval(pollInterval.current);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedSession) {
            loadMessages(selectedSession.id);
            // Poll for new messages every 3 seconds
            pollInterval.current = setInterval(() => {
                loadMessages(selectedSession.id, true);
            }, 3000);
        }
        return () => {
            if (pollInterval.current) {
                clearInterval(pollInterval.current);
            }
        };
    }, [selectedSession]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadSessions = async () => {
        try {
            const res = await chatAPI.getSessions();
            setSessions(res.data);
            
            // Auto-select active session or first upcoming
            const activeSession = res.data.find(s => s.is_active);
            if (activeSession) {
                setSelectedSession(activeSession);
            } else if (res.data.length > 0) {
                setSelectedSession(res.data[0]);
            }
        } catch (err) {
            console.error('Failed to load sessions:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadMessages = async (sessionId, silent = false) => {
        try {
            const res = await chatAPI.getMessages(sessionId);
            setMessages(res.data);
        } catch (err) {
            if (!silent) console.error('Failed to load messages:', err);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !user || !selectedSession) return;
        
        setSending(true);
        try {
            await chatAPI.sendMessage(selectedSession.id, { content: newMessage });
            setNewMessage('');
            await loadMessages(selectedSession.id, true);
        } catch (err) {
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleCreateSession = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error('Please login to create a session');
            return;
        }
        
        try {
            const res = await chatAPI.createSession({
                ...sessionForm,
                scheduled_at: new Date(sessionForm.scheduled_at).toISOString()
            });
            setSessions([res.data, ...sessions]);
            setShowCreateSession(false);
            setSessionForm({ title: '', scheduled_at: '', description: '' });
            toast.success('Chat session created!');
        } catch (err) {
            toast.error('Failed to create session');
        }
    };

    const toggleSessionActive = async (sessionId) => {
        try {
            const res = await chatAPI.toggleSession(sessionId);
            setSessions(sessions.map(s => 
                s.id === sessionId ? { ...s, is_active: res.data.is_active } : s
            ));
            if (selectedSession?.id === sessionId) {
                setSelectedSession({ ...selectedSession, is_active: res.data.is_active });
            }
            toast.success(res.data.is_active ? 'Session is now live!' : 'Session ended');
        } catch (err) {
            toast.error('Failed to update session');
        }
    };

    const formatSessionDate = (dateStr) => {
        const date = new Date(dateStr);
        return format(date, 'MMM d, yyyy • h:mm a');
    };

    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="grid lg:grid-cols-[320px_1fr] gap-6 h-[calc(100vh-12rem)]">
                {/* Sessions Sidebar */}
                <motion.aside
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex flex-col"
                >
                    <Card className="flex-1 flex flex-col overflow-hidden">
                        <div className="p-4 border-b">
                            <div className="flex items-center justify-between mb-2">
                                <h2 className="font-heading text-lg font-semibold">Chat Sessions</h2>
                                {user && (
                                    <Dialog open={showCreateSession} onOpenChange={setShowCreateSession}>
                                        <DialogTrigger asChild>
                                            <Button size="sm" variant="outline" className="rounded-full" data-testid="create-session-btn">
                                                <Plus className="h-4 w-4" />
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Schedule Chat Session</DialogTitle>
                                                <DialogDescription>
                                                    Create a new live chat session for your community
                                                </DialogDescription>
                                            </DialogHeader>
                                            <form onSubmit={handleCreateSession} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="session-title">Title</Label>
                                                    <Input
                                                        id="session-title"
                                                        value={sessionForm.title}
                                                        onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                                                        required
                                                        placeholder="Weekly Q&A Session"
                                                        data-testid="session-title-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="session-date">Date & Time</Label>
                                                    <Input
                                                        id="session-date"
                                                        type="datetime-local"
                                                        value={sessionForm.scheduled_at}
                                                        onChange={(e) => setSessionForm({ ...sessionForm, scheduled_at: e.target.value })}
                                                        required
                                                        data-testid="session-date-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="session-desc">Description (optional)</Label>
                                                    <Input
                                                        id="session-desc"
                                                        value={sessionForm.description}
                                                        onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })}
                                                        placeholder="What we'll discuss..."
                                                        data-testid="session-description-input"
                                                    />
                                                </div>
                                                <Button type="submit" className="w-full rounded-full" data-testid="submit-session-btn">
                                                    Create Session
                                                </Button>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Join our weekly discussions
                            </p>
                        </div>
                        
                        <ScrollArea className="flex-1">
                            {sessions.length === 0 ? (
                                <div className="p-6 text-center text-muted-foreground">
                                    <Calendar className="h-10 w-10 mx-auto mb-3 opacity-50" />
                                    <p className="text-sm">No sessions scheduled</p>
                                </div>
                            ) : (
                                <div className="p-2 space-y-1">
                                    {sessions.map((session) => (
                                        <button
                                            key={session.id}
                                            onClick={() => setSelectedSession(session)}
                                            className={`w-full p-3 rounded-lg text-left transition-colors ${
                                                selectedSession?.id === session.id
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'hover:bg-muted'
                                            }`}
                                            data-testid={`session-${session.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        {session.is_active && (
                                                            <Radio className="h-3 w-3 text-green-500 animate-pulse" />
                                                        )}
                                                        <p className="font-medium truncate text-sm">
                                                            {session.title}
                                                        </p>
                                                    </div>
                                                    <p className={`text-xs mt-1 ${
                                                        selectedSession?.id === session.id
                                                            ? 'opacity-80'
                                                            : 'text-muted-foreground'
                                                    }`}>
                                                        {formatSessionDate(session.scheduled_at)}
                                                    </p>
                                                </div>
                                                {session.is_active && (
                                                    <Badge variant="secondary" className="text-xs">Live</Badge>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </ScrollArea>
                    </Card>
                </motion.aside>

                {/* Chat Area */}
                <motion.main
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-col"
                >
                    {selectedSession ? (
                        <Card className="flex-1 flex flex-col overflow-hidden">
                            {/* Chat Header */}
                            <div className="p-4 border-b flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="font-heading text-lg font-semibold">
                                            {selectedSession.title}
                                        </h2>
                                        {selectedSession.is_active && (
                                            <Badge className="bg-green-500">Live</Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {formatSessionDate(selectedSession.scheduled_at)}
                                    </p>
                                </div>
                                {user && (
                                    <Button
                                        variant={selectedSession.is_active ? "destructive" : "default"}
                                        size="sm"
                                        className="rounded-full"
                                        onClick={() => toggleSessionActive(selectedSession.id)}
                                        data-testid="toggle-session-btn"
                                    >
                                        {selectedSession.is_active ? 'End Session' : 'Start Session'}
                                    </Button>
                                )}
                            </div>

                            {/* Messages */}
                            <ScrollArea className="flex-1 p-4">
                                {messages.length === 0 ? (
                                    <div className="h-full flex items-center justify-center text-muted-foreground">
                                        <div className="text-center">
                                            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                            <p>No messages yet</p>
                                            <p className="text-sm">Be the first to say hello!</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={`flex items-start gap-3 ${
                                                    msg.user_id === user?.id ? 'flex-row-reverse' : ''
                                                }`}
                                                data-testid={`message-${msg.id}`}
                                            >
                                                <Avatar className="h-8 w-8">
                                                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                                                        {getInitials(msg.user_name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className={`max-w-[70%] ${
                                                    msg.user_id === user?.id ? 'text-right' : ''
                                                }`}>
                                                    <p className="text-xs text-muted-foreground mb-1">
                                                        {msg.user_name}
                                                    </p>
                                                    <div className={`inline-block px-4 py-2 rounded-2xl ${
                                                        msg.user_id === user?.id
                                                            ? 'message-bubble-user'
                                                            : 'message-bubble-other'
                                                    }`}>
                                                        <p className="text-sm">{msg.content}</p>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {format(new Date(msg.created_at), 'h:mm a')}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}
                            </ScrollArea>

                            {/* Message Input */}
                            <div className="p-4 border-t">
                                {user ? (
                                    <form onSubmit={handleSendMessage} className="flex gap-2">
                                        <Input
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder="Type your message..."
                                            disabled={!selectedSession.is_active}
                                            className="flex-1"
                                            data-testid="chat-input"
                                        />
                                        <Button 
                                            type="submit" 
                                            className="rounded-full"
                                            disabled={!newMessage.trim() || sending || !selectedSession.is_active}
                                            data-testid="send-message-btn"
                                        >
                                            {sending ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Send className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </form>
                                ) : (
                                    <p className="text-center text-muted-foreground text-sm">
                                        Please <a href="/login" className="text-primary hover:underline">login</a> to participate in the chat
                                    </p>
                                )}
                                {user && !selectedSession.is_active && (
                                    <p className="text-center text-muted-foreground text-sm mt-2">
                                        Chat is not live. Wait for the session to start.
                                    </p>
                                )}
                            </div>
                        </Card>
                    ) : (
                        <Card className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                                <h2 className="font-heading text-xl font-semibold mb-2">
                                    Select a Session
                                </h2>
                                <p className="text-muted-foreground">
                                    Choose a chat session from the list to join the conversation
                                </p>
                            </div>
                        </Card>
                    )}
                </motion.main>
            </div>
        </div>
    );
}
